import { useMemo, useState } from 'react';
import {
  Activity, BarChart3, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, FileStack,
  Hash, Loader2, Pencil, Plus, RefreshCw, Sparkles, Tags, TrendingDown, TrendingUp, Trash2, Zap,
} from 'lucide-react';
import {
  Area, AreaChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { Button } from '../../components/ui/button';
import { DateRangePicker } from '../../components/ui/date-range-picker';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { cn } from '../../lib/utils';

// Lead-source filter options for the All Tag Records list. The "Status" column
// now reflects where the call's lead came from (AI-detected vs keyword match)
// rather than the Gemini pipeline state, so the filter mirrors that.
export const LEAD_SOURCE_OPTIONS = [
  { value: '', label: 'Lead Status' },
  { value: 'ai', label: 'AI Lead' },
  { value: 'keyword', label: 'Manual Lead' },
];

// A call counts as "analyzed by Claude" when call.analysis holds a real result
// (an overall_score). When true, the Score column shows the Claude score and the
// Model column reads "Claude"; otherwise both fall back to the Gemma pipeline.
function isAnalyzedByClaude(call) {
  return Boolean(call?.analysis && Object.keys(call.analysis).length > 0 && call.analysis.overall_score !== undefined && call.analysis.overall_score !== null);
}

// Small badge marking tags auto-created from AI-detected medical leads
// (heart, kidney, etc.) so they're distinct from manual keyword tags.
function AiLeadBadge({ source, className }) {
  if (source !== 'ai') {
    return null;
  }

  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-300', className)}>
      <Sparkles className="h-2.5 w-2.5" />
      AI Lead
    </span>
  );
}

function getPreviewTone(tone) {
  const tones = {
    teal: {
      chip: 'bg-teal-500/15 text-teal-300',
      button: 'bg-teal-500/10 text-teal-300 hover:bg-teal-500/20',
      border: 'border-teal-500/20',
    },
    sky: {
      chip: 'bg-sky-500/15 text-sky-300',
      button: 'bg-sky-500/10 text-sky-300 hover:bg-sky-500/20',
      border: 'border-sky-500/20',
    },
    violet: {
      chip: 'bg-violet-500/15 text-violet-300',
      button: 'bg-violet-500/10 text-violet-300 hover:bg-violet-500/20',
      border: 'border-violet-500/20',
    },
  };

  return tones[tone] || tones.teal;
}

// Collects the AI-detected medical leads for a call from both analysis sources
// (Claude `analysis` + Gemma `gemini_analysis`), de-duplicated by name.
function getAiMatchedLeads(call) {
  const sources = [call?.analysis?.medical_leads, call?.gemini_analysis?.medical_leads];
  const seen = new Set();
  const leads = [];

  for (const source of sources) {
    if (!Array.isArray(source)) {
      continue;
    }
    for (const item of source) {
      const name = String((typeof item === 'string' ? item : item?.lead) || '').trim();
      if (!name) {
        continue;
      }
      const key = name.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      leads.push(name);
    }
  }

  return leads;
}

function KeywordPreview({
  items,
  limit = 3,
  emptyLabel = 'Matches tag name only',
  title = 'Keywords',
  tone = 'teal',
}) {
  const values = Array.isArray(items) ? items.filter(Boolean) : [];
  const previewTone = getPreviewTone(tone);

  if (values.length === 0) {
    return <span className="text-xs text-muted-foreground">{emptyLabel}</span>;
  }

  const visibleItems = values.slice(0, limit);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visibleItems.map((item) => (
        <span key={item} className={cn('inline-flex rounded-md px-2 py-0.5 text-[10px] font-medium', previewTone.chip)}>
          {item}
        </span>
      ))}
      {values.length > limit && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'inline-flex rounded-md border px-2 py-0.5 text-[10px] font-medium transition-colors',
                previewTone.button,
                previewTone.border
              )}
            >
              +{values.length - limit} more
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 border-border bg-card p-3" onClick={(e) => e.stopPropagation()}>
            <div className="space-y-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{values.length} total</p>
              </div>
              <div className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto">
                {values.map((item) => (
                  <span key={item} className={cn('inline-flex rounded-md px-2 py-1 text-[11px] font-medium', previewTone.chip)}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function CountBar({ value, max, subtitle }) {
  const normalizedValue = Number(value) || 0;
  const normalizedMax = Math.max(Number(max) || 0, 1);
  const width = Math.max(6, Math.round((normalizedValue / normalizedMax) * 100));

  return (
    <div className="min-w-[180px]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold tabular-nums">{normalizedValue}</span>
        {subtitle && <span className="text-[11px] text-muted-foreground">{subtitle}</span>}
      </div>
      <div className="mt-2 h-2 rounded-full bg-background/80 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-teal-500 via-sky-500 to-cyan-400"
          style={{ width: `${normalizedValue > 0 ? width : 0}%` }}
        />
      </div>
    </div>
  );
}

const openCallDetailsInNewTab = (callId, e) => {
  e.stopPropagation();
  const detailPath = `/calls/${encodeURIComponent(callId)}`;

  const newTab = window.open('', '_blank');
  if (!newTab) {
    window.location.href = detailPath;
    return;
  }

  try {
    const token = sessionStorage.getItem('vcm_token');
    const userData = sessionStorage.getItem('vcm_user');
    if (token) {
      newTab.sessionStorage.setItem('vcm_token', token);
    }
    if (userData) {
      newTab.sessionStorage.setItem('vcm_user', userData);
    }
    newTab.location.href = detailPath;
  } catch (error) {
    newTab.location.href = detailPath;
  }
};

function getKeywordCount(tag, keyword) {
  const counts = tag?.keyword_counts || {};
  const directKey = String(keyword || '');
  const normalizedKey = directKey
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return Number(counts[directKey] ?? counts[directKey.toLowerCase()] ?? counts[normalizedKey] ?? 0);
}

function getTagCallRowKey(call, selectedTagId) {
  return `${call.call_id}-${call.tag_id || selectedTagId || 'all'}`;
}

const TAG_MENU_TONES = [
  {
    icon: 'bg-teal-500/15 text-teal-300',
    row: 'hover:bg-teal-500/10',
    active: 'border-teal-400/40 bg-teal-500/12',
    count: 'bg-teal-500/15 text-teal-300',
    panel: 'from-teal-500/12 via-background/40 to-cyan-500/10',
    keyword: 'hover:border-teal-400/35 hover:bg-teal-500/10',
    keywordActive: 'border-teal-400/45 bg-teal-500/15 text-teal-100',
  },
  {
    icon: 'bg-sky-500/15 text-sky-300',
    row: 'hover:bg-sky-500/10',
    active: 'border-sky-400/40 bg-sky-500/12',
    count: 'bg-sky-500/15 text-sky-300',
    panel: 'from-sky-500/12 via-background/40 to-indigo-500/10',
    keyword: 'hover:border-sky-400/35 hover:bg-sky-500/10',
    keywordActive: 'border-sky-400/45 bg-sky-500/15 text-sky-100',
  },
  {
    icon: 'bg-amber-500/15 text-amber-300',
    row: 'hover:bg-amber-500/10',
    active: 'border-amber-400/40 bg-amber-500/12',
    count: 'bg-amber-500/15 text-amber-300',
    panel: 'from-amber-500/12 via-background/40 to-orange-500/10',
    keyword: 'hover:border-amber-400/35 hover:bg-amber-500/10',
    keywordActive: 'border-amber-400/45 bg-amber-500/15 text-amber-100',
  },
  {
    icon: 'bg-rose-500/15 text-rose-300',
    row: 'hover:bg-rose-500/10',
    active: 'border-rose-400/40 bg-rose-500/12',
    count: 'bg-rose-500/15 text-rose-300',
    panel: 'from-rose-500/12 via-background/40 to-pink-500/10',
    keyword: 'hover:border-rose-400/35 hover:bg-rose-500/10',
    keywordActive: 'border-rose-400/45 bg-rose-500/15 text-rose-100',
  },
];

function getTagMenuTone(tag) {
  const key = Number(tag?.id) || String(tag?.name || '').length;
  return TAG_MENU_TONES[Math.abs(key) % TAG_MENU_TONES.length];
}

export function TagsSnapshot({ totalTags, activeTags, taggedCalls }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-border bg-background/40 px-3 py-2">
          <div className="flex items-center gap-2 text-teal-400">
            <Tags className="w-3.5 h-3.5" />
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Total Tags</span>
          </div>
          <p className="mt-1 text-xl font-semibold tabular-nums">{totalTags}</p>
        </div>
        <div className="rounded-lg border border-border bg-background/40 px-3 py-2">
          <div className="flex items-center gap-2 text-emerald-400">
            <BarChart3 className="w-3.5 h-3.5" />
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Active Tags</span>
          </div>
          <p className="mt-1 text-xl font-semibold tabular-nums">{activeTags}</p>
        </div>
        <div className="rounded-lg border border-border bg-background/40 px-3 py-2">
          <div className="flex items-center gap-2 text-sky-400">
            <FileStack className="w-3.5 h-3.5" />
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">All Tag Records</span>
          </div>
          <p className="mt-1 text-xl font-semibold tabular-nums">{taggedCalls}</p>
        </div>
      </div>
    </div>
  );
}

// Small KPI tile for the Tags dashboard, mirroring the main dashboard's cards.
function DashKpi({ icon: Icon, label, value, sub, trend, tone = 'teal' }) {
  const tones = {
    teal: 'text-teal-400 bg-teal-500/10',
    sky: 'text-sky-400 bg-sky-500/10',
    violet: 'text-violet-400 bg-violet-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    emerald: 'text-emerald-400 bg-emerald-500/10',
  };
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone] || tones.teal}`}>
          <Icon className="w-4 h-4" />
        </div>
        {trend !== undefined && trend !== null && (
          <span className={`flex items-center gap-0.5 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function DashChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-2 shadow-lg">
      <p className="text-xs font-medium mb-1">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
}

// Exact accent from the dashboard "Purposes" list (teal accent of
// CompactDashboardList) reused here for a consistent look.
const PURPOSES_CHIP = 'bg-teal-500/15 text-teal-300';
const PURPOSES_BAR = 'from-teal-500 via-cyan-500 to-sky-500';
// Lead-source donut slice colours — the teal/sky endpoints of the Purposes
// gradient, so the pie stays on the same palette.
const LEAD_PIE_COLORS = { ai: '#22D3C5', keyword: '#2BB6FF' };

// Tags page dashboard: a dedicated analytics view (KPIs, tagged-call trend,
// frequently used tags and trending tags) built from /tags/analytics. Layout
// and styling take reference from the main DashboardPage.
export function TagsDashboardSection({
  dateRange,
  isCustomDateRange,
  onDateRangeChange,
  onDateRangeClear,
  refreshing,
  onRefresh,
  loading,
  analytics,
  allTagRecords = 0,
  rangeLabel,
}) {
  const kpis = analytics?.kpis || {};
  const dailyTrend = Array.isArray(analytics?.daily_trend) ? analytics.daily_trend : [];
  const frequentlyUsed = Array.isArray(analytics?.frequently_used) ? analytics.frequently_used : [];
  const trending = Array.isArray(analytics?.trending) ? analytics.trending : [];

  const maxFrequent = frequentlyUsed.reduce((max, tag) => Math.max(max, Number(tag.count) || 0), 0);
  const aiCalls = Number(kpis.ai_calls) || 0;
  const keywordCalls = Number(kpis.keyword_calls) || 0;
  const leadTotal = aiCalls + keywordCalls;
  const leadSplit = [
    { key: 'ai', name: 'AI Lead', value: aiCalls },
    { key: 'keyword', name: 'Keyword Lead', value: keywordCalls },
  ].filter((entry) => entry.value > 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card border border-border rounded-xl px-3 py-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-teal-400" />
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Tags Dashboard</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Tag activity overview for {rangeLabel}.</p>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <DateRangePicker
              value={dateRange}
              showValue={isCustomDateRange}
              placeholder="Select date range"
              onChange={onDateRangeChange}
              onClear={onDateRangeClear}
              buttonClassName="h-auto min-w-[220px] max-w-[300px] py-2"
            />
            <Button size="sm" variant="ghost" onClick={onRefresh} disabled={refreshing}>
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* KPI cards — all in a single row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
            <DashKpi icon={Tags} label="Total Tags" value={kpis.total_tags || 0} sub={`${kpis.active_tags || 0} active`} tone="teal" />
            <DashKpi icon={FileStack} label="All Tag Records" value={allTagRecords} sub="across all tags" tone="sky" />
            <DashKpi icon={Sparkles} label="AI Lead Tags" value={kpis.ai_tags || 0} tone="violet" />
            <DashKpi icon={Hash} label="Keyword Tags" value={kpis.keyword_tags || 0} tone="sky" />
            <DashKpi
              icon={Activity}
              label="Tagged Calls"
              value={kpis.tagged_calls || 0}
              sub={`vs ${kpis.previous_tagged_calls || 0} prev`}
              trend={kpis.tagged_calls_delta_pct}
              tone="emerald"
            />
            <DashKpi icon={Sparkles} label="AI Lead Calls" value={aiCalls} tone="violet" />
            <DashKpi icon={Activity} label="Keyword Calls" value={keywordCalls} tone="amber" />
          </div>

          {/* Trend + lead split */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Tagged Calls Trend</h3>
              {dailyTrend.length > 0 ? (
                <ResponsiveContainer className="focus:outline-none" width="100%" height={260}>
                  <AreaChart data={dailyTrend}>
                    <defs>
                      <linearGradient id="tagsTrendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22D3C5" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#22D3C5" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,34%,20%)" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#A6B2C7' }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#A6B2C7' }} />
                    <Tooltip content={<DashChartTooltip />} />
                    <Area type="monotone" dataKey="count" stroke="#22D3C5" fill="url(#tagsTrendGrad)" name="Tagged calls" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
                  No tagged calls in this window.
                </div>
              )}
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Lead Source Split</h3>
                <span className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-medium ${PURPOSES_CHIP}`}>{leadTotal} calls</span>
              </div>
              {leadTotal > 0 ? (
                <>
                  <div className="relative">
                    <ResponsiveContainer className="focus:outline-none" width="100%" height={180}>
                      <PieChart>
                        <Pie data={leadSplit} cx="50%" cy="50%" innerRadius={56} outerRadius={74} paddingAngle={3} dataKey="value" stroke="none">
                          {leadSplit.map((entry) => (
                            <Cell key={entry.key} fill={LEAD_PIE_COLORS[entry.key]} />
                          ))}
                        </Pie>
                        <Tooltip content={<DashChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xl font-semibold tabular-nums text-foreground/90">{leadTotal}</span>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">lead calls</span>
                    </div>
                  </div>
                  <div className="space-y-1.5 mt-3">
                    {leadSplit.map((entry) => {
                      const pct = leadTotal > 0 ? ((entry.value / leadTotal) * 100).toFixed(1) : 0;
                      return (
                        <div key={entry.key} className="flex items-center justify-between gap-2 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: LEAD_PIE_COLORS[entry.key] }} />
                            <span className="font-medium text-foreground">{entry.name}</span>
                          </div>
                          <span className="tabular-nums text-muted-foreground">{entry.value} ({pct}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground py-4 text-center">No lead data for this window.</p>
              )}
            </div>
          </div>

          {/* Frequently used + trending */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Frequently Used Tags</h3>
                <span className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-medium ${PURPOSES_CHIP}`}>{frequentlyUsed.length} tags</span>
              </div>
              <div className="space-y-2.5">
                {frequentlyUsed.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-4 text-center">No tag matches in this window.</p>
                ) : frequentlyUsed.map((tag) => {
                  const width = maxFrequent > 0 ? Math.max(8, Math.round((tag.count / maxFrequent) * 100)) : 0;
                  const isAi = tag.source === 'ai';
                  return (
                    <div key={tag.id} className="space-y-1.5">
                      <div className="flex items-center justify-between gap-3 text-xs">
                        <span className="flex items-center gap-1.5 truncate">
                          {isAi && <Sparkles className="h-3 w-3 flex-shrink-0 text-teal-300" />}
                          <span className="truncate capitalize text-foreground">{tag.name}</span>
                        </span>
                        <span className="tabular-nums text-muted-foreground">{tag.count}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-background/80">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${PURPOSES_BAR}`}
                          style={{ width: `${width}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Trending Tags</h3>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">vs previous period</span>
              </div>
              <div className="space-y-2">
                {trending.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No trend data in this window.</p>
                ) : trending.map((tag, index) => {
                  const up = tag.delta >= 0;
                  return (
                    <div key={tag.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/[0.03] transition-colors">
                      <span className="text-xs font-mono text-muted-foreground w-5">{index + 1}</span>
                      <span className="flex items-center gap-1.5 flex-1 min-w-0">
                        {tag.source === 'ai' && <Sparkles className="w-3 h-3 text-violet-300 flex-shrink-0" />}
                        <span className="text-sm truncate capitalize">{tag.name}</span>
                      </span>
                      <span className="text-xs text-muted-foreground tabular-nums">{tag.previous} → {tag.current}</span>
                      <span className={`flex items-center gap-0.5 text-xs font-medium px-2 py-0.5 rounded ${up ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>
                        {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {up ? '+' : ''}{tag.delta}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Brief explainer */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">What this shows</h3>
            <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
              <li><span className="text-foreground font-medium">Tagged Calls</span> — distinct calls that matched at least one tag in {rangeLabel}, with the change vs the previous equal-length period.</li>
              <li><span className="text-foreground font-medium">Lead Source Split</span> — how many tagged calls came from <span className="text-violet-300">AI-detected leads</span> vs <span className="text-sky-300">keyword matches</span>.</li>
              <li><span className="text-foreground font-medium">Frequently Used Tags</span> — the tags matching the most calls in this window (volume).</li>
              <li><span className="text-foreground font-medium">Trending Tags</span> — tags with the largest increase in matched calls versus the previous period.</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export function TagsOverviewSection({
  dateRange,
  isCustomDateRange,
  onDateRangeChange,
  onDateRangeClear,
  refreshing,
  onRefresh,
  tagSearch,
  setTagSearch,
  filteredSummaryTags,
  hasAnySummaryTags,
  selectedTagId,
  onSelectTag,
  selectedTagMeta,
  selectedKeyword,
  selectedTagCallsTotal,
  dateRangeLabel,
  callFilters,
  setCallFilters,
  callsLoading,
  selectedTagCalls,
  getScoreBadge,
  analyzingCalls,
  // onAnalyzeCall,
  selectedTagCallsPages,
  tagCallsPage,
  setTagCallsPage,
  callsSectionRef,
}) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [activeMenuTagId, setActiveMenuTagId] = useState(null);

  const groupedTags = useMemo(() => {
    const groups = filteredSummaryTags.reduce((map, tag) => {
      const letter = String(tag?.name || '').trim().charAt(0).toUpperCase() || '#';
      const safeLetter = /[A-Z]/.test(letter) ? letter : '#';
      if (!map.has(safeLetter)) {
        map.set(safeLetter, []);
      }
      map.get(safeLetter).push(tag);
      return map;
    }, new Map());

    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([letter, tags]) => ({
        letter,
        tags: [...tags].sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [filteredSummaryTags]);

  const activeMenuTag = useMemo(() => (
    filteredSummaryTags.find((tag) => tag.id === (activeMenuTagId || selectedTagId))
    || filteredSummaryTags[0]
    || null
  ), [activeMenuTagId, filteredSummaryTags, selectedTagId]);

  return (
    <div className="space-y-3">
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-3 py-3 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Tags className="w-4 h-4 text-teal-400" />
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Tag Selector</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Search by tag or keyword, then open matched calls.</p>
              </div>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <Popover open={selectorOpen} onOpenChange={setSelectorOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-auto w-full justify-between border-primary/20 bg-primary/10 px-3 py-2 text-left text-primary hover:bg-primary/15 hover:text-primary sm:min-w-[300px] sm:max-w-[400px]">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-primary">
                        {selectedTagMeta?.name
                          ? `${selectedTagMeta.name}${selectedKeyword ? ` / ${selectedKeyword}` : ''}`
                          : 'All Tag Records'}
                      </p>
                      <p className="mt-0.5 text-[11px] text-primary/75">
                        {selectedTagMeta
                          ? `${selectedTagCallsTotal} calls in selected range`
                          : `${selectedTagCallsTotal} tag records in selected range`}
                      </p>
                    </div>
                    <div className="ml-3 flex items-center gap-2 shrink-0">
                      {/* <span className="rounded-md bg-primary/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-primary">Browse</span> */}
                      <ChevronDown className="h-4 w-4 text-primary" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={6} className="w-[min(92vw,680px)] border-primary/20 bg-primary/10 p-0 shadow-xl shadow-primary/5 backdrop-blur">
                  <div className="grid max-h-[min(72vh,440px)] grid-cols-1 overflow-hidden rounded-md md:grid-cols-[280px_1fr]">
                    <div className="border-b border-primary/15 bg-background/45 p-2.5 md:border-b-0 md:border-r">
                      <Input
                        placeholder="Search tags or keywords..."
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        className="h-8 border-primary/20 bg-background/75"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          onSelectTag(null, '');
                          setSelectorOpen(false);
                        }}
                        className={cn(
                          'mt-2 flex w-full items-center justify-between gap-3 rounded-lg border px-2.5 py-2 text-left transition-colors',
                          !selectedTagId ? 'border-primary/40 bg-primary/15 text-primary' : 'border-primary/15 bg-background/45 hover:bg-primary/10'
                        )}
                      >
                        <span>
                          <span className="block text-sm font-medium">All Tag Records</span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">Show every matched tag record</span>
                        </span>
                        <span className="rounded-md bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary">All</span>
                      </button>
                      <div className="mt-2 max-h-[312px] space-y-1.5 overflow-auto pr-1">
                        {!hasAnySummaryTags ? (
                          <p className="py-6 text-center text-sm text-muted-foreground">No active tags created yet.</p>
                        ) : filteredSummaryTags.length === 0 ? (
                          <p className="py-6 text-center text-sm text-muted-foreground">No tags or keywords match this search.</p>
                        ) : groupedTags.map((group) => (
                          <div key={group.letter} className="rounded-lg border border-primary/15 bg-background/45">
                            <div className="flex items-center justify-between gap-3 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              <span>{group.letter}</span>
                              <span className="rounded-md bg-background px-2 py-0.5 text-[10px] text-muted-foreground">{group.tags.length}</span>
                            </div>
                            <div className="space-y-1 border-t border-border p-1">
                              {group.tags.map((tag) => {
                                const isSelected = selectedTagId === tag.id && !selectedKeyword;
                                const isActive = activeMenuTag?.id === tag.id;
                                const tone = getTagMenuTone(tag);

                                return (
                                  <button
                                    key={tag.id}
                                    type="button"
                                    onMouseEnter={() => setActiveMenuTagId(tag.id)}
                                    onFocus={() => setActiveMenuTagId(tag.id)}
                                    onClick={() => {
                                      onSelectTag(tag.id, '');
                                      setSelectorOpen(false);
                                    }}
                                    className={cn(
                                      'flex w-full items-center justify-between gap-3 rounded-lg border px-2.5 py-2 text-left transition-colors',
                                      isActive ? tone.active : `border-transparent ${tone.row}`,
                                      isSelected && 'ring-1 ring-primary/40'
                                    )}
                                  >
                                    <span className="flex min-w-0 items-center gap-2">
                                      <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold uppercase', tone.icon)}>
                                        {String(tag.name || '?').charAt(0)}
                                      </span>
                                      <span className="min-w-0">
                                        <span className="flex items-center gap-1.5">
                                          <span className="truncate text-sm font-medium text-foreground">{tag.name}</span>
                                          <AiLeadBadge source={tag.source} />
                                        </span>
                                        <span className="mt-0.5 block text-xs text-muted-foreground">
                                          {tag.source === 'ai'
                                            ? 'AI-detected lead'
                                            : `${(tag.keywords || []).length} keyword${(tag.keywords || []).length === 1 ? '' : 's'}`}
                                        </span>
                                      </span>
                                    </span>
                                    <span className={cn('shrink-0 rounded-md px-2 py-0.5 text-[10px] font-medium', tone.count)}>
                                      {tag.count || 0}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className={cn('min-h-[220px] bg-gradient-to-br p-3', activeMenuTag ? getTagMenuTone(activeMenuTag).panel : 'from-primary/10 via-background/35 to-primary/5')}>
                      {activeMenuTag ? (
                        <div className="space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Selected Tag</p>
                              <h4 className="mt-1 truncate text-base font-semibold text-foreground">{activeMenuTag.name}</h4>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {activeMenuTag.count || 0} call{Number(activeMenuTag.count || 0) === 1 ? '' : 's'} in selected range
                              </p>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => {
                                onSelectTag(activeMenuTag.id, '');
                                setSelectorOpen(false);
                              }}
                            >
                              View All
                            </Button>
                          </div>
                          {(activeMenuTag.keywords || []).length > 0 ? (
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Keyword Extension</p>
                              <p className="mt-0.5 text-xs text-muted-foreground">Pick a keyword to show exact matched calls.</p>
                              <div className="mt-2 grid max-h-[300px] grid-cols-1 gap-2 overflow-auto pr-1 sm:grid-cols-2">
                                {(activeMenuTag.keywords || []).map((keyword) => {
                                  const isKeywordSelected = selectedTagId === activeMenuTag.id && selectedKeyword === keyword;
                                  const keywordCount = getKeywordCount(activeMenuTag, keyword);
                                  const tone = getTagMenuTone(activeMenuTag);
                                  return (
                                    <button
                                      key={keyword}
                                      type="button"
                                      onClick={() => {
                                        onSelectTag(activeMenuTag.id, keyword);
                                        setSelectorOpen(false);
                                      }}
                                      className={cn(
                                        'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                                        isKeywordSelected
                                          ? tone.keywordActive
                                          : `border-border bg-card ${tone.keyword}`
                                      )}
                                    >
                                      <span className="block truncate font-medium">{keyword}</span>
                                      <span className="mt-1 block text-[11px] text-muted-foreground">
                                        {keywordCount} matched call{keywordCount === 1 ? '' : 's'}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-xl border border-dashed border-border bg-card/60 p-4 text-sm text-muted-foreground">
                              This tag matches by tag name only. Use View All to see related calls.
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                          Select a tag to view keyword options.
                        </div>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <div className="flex items-center gap-2">
                <DateRangePicker
                  value={dateRange}
                  showValue={isCustomDateRange}
                  placeholder="Select date range"
                  onChange={onDateRangeChange}
                  onClear={onDateRangeClear}
                  buttonClassName="h-auto min-w-[220px] max-w-[300px] py-2"
                />
                <Button size="sm" variant="ghost" onClick={onRefresh} disabled={refreshing}>
                  {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div ref={callsSectionRef} className="bg-card border border-border rounded-xl overflow-hidden scroll-mt-20">
        <div className="px-3 py-3 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {selectedTagMeta
                  ? `${selectedTagMeta.name}${selectedKeyword ? ` / ${selectedKeyword}` : ''} Calls`
                  : 'All Tag Records'}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedTagMeta
                  ? `${selectedTagCallsTotal} related call${selectedTagCallsTotal === 1 ? '' : 's'}${selectedKeyword ? ` for keyword "${selectedKeyword}"` : ''} for ${dateRangeLabel}`
                  : `${selectedTagCallsTotal} tag record${selectedTagCallsTotal === 1 ? '' : 's'} for ${dateRangeLabel}. Select a tag to narrow the list.`}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selectedTagMeta && (
                <span className="rounded-md bg-sky-500/15 px-3 py-1 text-xs font-medium text-sky-300">
                  {selectedTagCallsTotal} result{selectedTagCallsTotal === 1 ? '' : 's'}
                </span>
              )}
              {selectedKeyword && (
                <span className="rounded-md bg-teal-500/15 px-3 py-1 text-xs font-medium text-teal-300">
                  Keyword: {selectedKeyword}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/40 px-3 py-1 text-xs text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5 text-sky-300" />
                {dateRangeLabel}
              </span>
            </div>
          </div>
          {selectedTagMeta && (selectedTagMeta.keywords || []).length > 0 && (
            <div className="mt-3">
              <KeywordPreview items={selectedTagMeta.keywords} limit={2} title={`${selectedTagMeta.name} keywords`} />
            </div>
          )}
        </div>
        <div className="px-3 py-2.5 border-b border-border bg-background/30">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <Input
              placeholder="Search call ID, agent, caller..."
              value={callFilters.search}
              onChange={(e) => setCallFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            />
            <select
              value={callFilters.direction}
              onChange={(e) => setCallFilters((prev) => ({ ...prev, direction: e.target.value }))}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Directions</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
            <select
              value={callFilters.lead_source}
              onChange={(e) => setCallFilters((prev) => ({ ...prev, lead_source: e.target.value }))}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            >
              {LEAD_SOURCE_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              value={callFilters.escalation}
              onChange={(e) => setCallFilters((prev) => ({ ...prev, escalation: e.target.value }))}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Escalations</option>
              <option value="true">Detected</option>
              <option value="false">Not Detected</option>
            </select>
            <select
              value={callFilters.sort_by}
              onChange={(e) => setCallFilters((prev) => ({ ...prev, sort_by: e.target.value }))}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            >
              <option value="created_at">Sort: Time</option>
              <option value="effective_score">Sort: Score</option>
              <option value="call_duration">Sort: Duration</option>
              <option value="agent_name">Sort: Agent</option>
            </select>
            <select
              value={callFilters.sort_order}
              onChange={(e) => setCallFilters((prev) => ({ ...prev, sort_order: e.target.value }))}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            >
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
            <Button
              variant="ghost"
              onClick={() => setCallFilters({ search: '', direction: '', lead_source: '', escalation: '', sort_by: 'created_at', sort_order: 'desc' })}
            >
              Clear
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Time</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Agent</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Caller</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Direction</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Duration</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Lead Status</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Score</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Model</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">AI Matched Leads</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Manual Matched Leads</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {callsLoading ? (
                <tr><td colSpan={11} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></td></tr>
              ) : selectedTagCalls.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-12 text-sm text-muted-foreground">
                  {selectedTagId ? 'No matched calls found for this tag in the selected date range.' : 'No tag records found in the selected date range.'}
                </td></tr>
              ) : selectedTagCalls.map((call) => {
                const rowKey = getTagCallRowKey(call, selectedTagId);
                const claudeScore = call.analysis?.overall_score;
                const gemmaScore = call.gemini_analysis?.overall_score;
                // Single combined score: Claude score when the call was analyzed
                // by Claude, otherwise the Gemma score. The Model column labels
                // which source the displayed score came from.
                const analyzedByClaude = isAnalyzedByClaude(call);
                const effectiveScore = analyzedByClaude ? claudeScore : gemmaScore;
                const effectiveScoreBadge = getScoreBadge(effectiveScore);
                const modelLabel = analyzedByClaude ? 'Claude' : 'Gemma';
                const processingStatus = String(call.processing_status || '').toLowerCase();
                const rawAnalysis = call.analysis;
                const hasAnalysisPayload = (() => {
                  if (!rawAnalysis) return false;
                  if (typeof rawAnalysis === 'string') {
                    const normalized = rawAnalysis.trim();
                    return normalized !== '' && normalized !== '{}' && normalized !== 'null';
                  }
                  return Object.keys(rawAnalysis).length > 0;
                })();
                const showAnalyzeButton = processingStatus !== 'completed' || !hasAnalysisPayload;
                const analyzeBusy = analyzingCalls.has(rowKey);
                const isAnalysisInProgress = processingStatus === 'analyzing' || processingStatus === 'voice_analyzing';

                return (
                  <tr
                    key={rowKey}
                    className="border-b border-border/50 hover:bg-white/[0.02] transition-colors cursor-pointer"
                    onClick={(e) => openCallDetailsInNewTab(call.call_id, e)}
                  >
                    <td className="px-4 py-3"><span className="text-sm">{call.date}</span><span className="text-xs text-muted-foreground ml-1">{call.time}</span></td>
                    <td className="px-4 py-3 text-sm">{call.agent_name}</td>
                    <td className="px-4 py-3 text-sm font-mono text-muted-foreground">{call.client_number}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${call.direction === 'inbound' ? 'bg-sky-500/15 text-sky-300' : 'bg-teal-500/15 text-teal-300'}`}>{call.direction}</span></td>
                    <td className="px-4 py-3 text-sm tabular-nums">{call.call_duration}s</td>
                    <td className="px-4 py-3">
                      {(() => {
                        // Status reflects where the call's lead originated: AI-detected
                        // (medical_leads from analysis/gemini_analysis) and/or keyword
                        // matches (matched_phrases). Show a badge per source present.
                        const hasAiLead = getAiMatchedLeads(call).length > 0;
                        const hasKeywordLead = Array.isArray(call.matched_phrases) && call.matched_phrases.length > 0;
                        if (!hasAiLead && !hasKeywordLead) {
                          return <span className="text-xs text-muted-foreground">-</span>;
                        }
                        return (
                          <div className="flex flex-wrap items-center gap-1">
                            {hasAiLead && (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-violet-500/15 text-violet-300">
                                <Sparkles className="h-2.5 w-2.5" />
                                AI Lead
                              </span>
                            )}
                            {hasKeywordLead && (
                              <span className="text-xs px-2 py-0.5 rounded bg-sky-500/15 text-sky-300">Manual Lead</span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      {effectiveScore !== null && effectiveScore !== undefined ? (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${effectiveScoreBadge.cls}`} title={`${modelLabel}: ${effectiveScoreBadge.label}`}>{effectiveScore} - {effectiveScoreBadge.label}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${analyzedByClaude ? 'bg-violet-500/15 text-violet-300' : 'bg-sky-500/15 text-sky-300'}`}>{modelLabel}</span>
                    </td>
                    <td className="px-4 py-3 max-w-[260px]">
                      <KeywordPreview items={getAiMatchedLeads(call)} limit={2} title="AI matched leads" emptyLabel="-" tone="violet" />
                    </td>
                    <td className="px-4 py-3 max-w-[260px]">
                      <KeywordPreview items={call.matched_phrases} limit={2} title="Manual matched leads" emptyLabel="-" tone="sky" />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div
                        className="flex items-center justify-end gap-1"
                        // onClick={(e) => e.stopPropagation()}
                        // onMouseDown={(e) => e.stopPropagation()}
                      >
                        {false && showAnalyzeButton && (
                          <Button
                            type="button"
                            size="sm"
                            variant="default"
                            className="h-7 text-xs"
                            disabled={analyzeBusy || isAnalysisInProgress}
                            onClick={(e) => onAnalyzeCall(e, call.call_id, rowKey)}
                          >
                            {analyzeBusy
                              ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Analyzing</>
                              : <><Zap className="w-3.5 h-3.5 mr-1" />Analyze</>}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={(e) => openCallDetailsInNewTab(call.call_id, e)}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {selectedTagCallsPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <span className="text-xs text-muted-foreground">Page {tagCallsPage} of {selectedTagCallsPages} ({selectedTagCallsTotal} {selectedTagId ? 'calls' : 'tag records'})</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={tagCallsPage <= 1} onClick={() => setTagCallsPage((page) => page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" disabled={tagCallsPage >= selectedTagCallsPages} onClick={() => setTagCallsPage((page) => page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ManageTagsSection({
  sortedTags,
  maxManagedMatches,
  onOpenCreateTagDialog,
  onEditTag,
  onDeleteTag,
  tagActionId,
}) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-4 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Tags className="w-4 h-4 text-primary" />
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Tag List</h3>
            <p className="text-xs text-muted-foreground mt-1">Edit tags inside a popup and keep status control with the tag form.</p>
          </div>
        </div>
        <Button size="sm" onClick={onOpenCreateTagDialog}>
          <Plus className="w-4 h-4 mr-1" />
          Add Tag
        </Button>
      </div>

      <div className="max-h-[520px] overflow-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Tag</th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Keywords</th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Matched Calls</th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Status</th>
              <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedTags.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-sm text-muted-foreground text-center py-10">No tags created yet.</td>
              </tr>
            ) : sortedTags.map((tag) => (
              <tr key={tag.id} className="border-b border-border/50 hover:bg-white/[0.02] transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg border border-border bg-background/50 text-teal-400 flex items-center justify-center">
                      <Tags className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{tag.name}</p>
                        <AiLeadBadge source={tag.source} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {tag.source === 'ai'
                          ? 'AI-detected medical lead'
                          : `${(tag.keywords || []).length} keyword${(tag.keywords || []).length === 1 ? '' : 's'}`}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 max-w-[340px]">
                  <KeywordPreview items={tag.keywords} limit={2} title={`${tag.name} keywords`} />
                </td>
                <td className="px-4 py-3">
                  <CountBar value={tag.matched_calls || 0} max={maxManagedMatches} subtitle="Last 3 months" />
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-md px-2 py-1 text-[10px] font-medium ${tag.is_active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-500/15 text-slate-300'}`}>
                    {tag.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onEditTag(tag)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-rose-400"
                      disabled={tagActionId === tag.id}
                      onClick={() => onDeleteTag(tag)}
                    >
                      {tagActionId === tag.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TagEditDialog({
  open,
  onOpenChange,
  editingTagId,
  tagForm,
  setTagForm,
  savingTag,
  onSaveTag,
}) {
  const isBusy = savingTag;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-border bg-card">
        <DialogHeader>
          <DialogTitle>{editingTagId ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
          <DialogDescription>
            {editingTagId
              ? 'Update keywords and status in one popup, then rematch calls automatically.'
              : 'Create a new tag with one or more keywords. New tags start as active.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSaveTag} className="space-y-4">
          {savingTag && (
            <div className="flex items-start gap-3 rounded-xl border border-teal-500/20 bg-teal-500/10 px-4 py-3">
              <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-teal-300" />
              <div>
                <p className="text-sm font-medium text-teal-100">
                  {String(tagForm.name || '').trim() || 'Tag'} data is loading
                </p>
                <p className="mt-1 text-xs text-teal-100/80">Please wait while we save the tag and finish loading its matched call data.</p>
              </div>
            </div>
          )}

          {editingTagId && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => setTagForm((prev) => ({ ...prev, is_active: true }))}
                  className={cn(
                    'rounded-xl border px-3 py-3 text-left transition-colors',
                    tagForm.is_active ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-border bg-background/40 hover:bg-white/[0.02]',
                    isBusy && 'pointer-events-none opacity-60'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">Active</span>
                    {tagForm.is_active && <Check className="w-4 h-4 text-emerald-400" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Included in new tag matching.</p>
                </button>
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => setTagForm((prev) => ({ ...prev, is_active: false }))}
                  className={cn(
                    'rounded-xl border px-3 py-3 text-left transition-colors',
                    !tagForm.is_active ? 'border-slate-400/40 bg-slate-500/10' : 'border-border bg-background/40 hover:bg-white/[0.02]',
                    isBusy && 'pointer-events-none opacity-60'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">Inactive</span>
                    {!tagForm.is_active && <Check className="w-4 h-4 text-slate-300" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Stops matching and removes future usage.</p>
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tag Name</label>
            <Input
              placeholder="Tag name, e.g. Heart Surgery"
              value={tagForm.name}
              disabled={isBusy}
              onChange={(e) => setTagForm((prev) => ({ ...prev, name: e.target.value }))}
              className="bg-background"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Keywords</label>
            <Textarea
              placeholder="Keywords or phrases. Use commas or one per line, e.g. heart surgery, cardiac surgery"
              value={tagForm.keywords}
              disabled={isBusy}
              onChange={(e) => setTagForm((prev) => ({ ...prev, keywords: e.target.value }))}
              className="bg-background min-h-[120px]"
            />
            <p className="text-[11px] text-muted-foreground">Only the first 2 keywords are shown in the tables. Extra items stay available in the popup.</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isBusy}>
              Cancel
            </Button>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {savingTag ? 'Loading Tag Data' : (editingTagId ? 'Update Tag' : 'Create Tag')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
